import {PaceRideTypes} from '@js/components/modules/constants';

const NAMESAKE_RADIUS_KM = 5;

const _normalizeName = (name) => (name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const _distanceKm = ([lngA, latA], [lngB, latB]) => {
    const toRad = (degree) => degree * Math.PI / 180;
    const dLat = toRad(latB - latA);
    const dLng = toRad(lngB - lngA);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(latA)) * Math.cos(toRad(latB)) * Math.sin(dLng / 2) ** 2;

    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Photon extent is [minLng, maxLat, maxLng, minLat]: read it order-agnostic
const _inBbox = (bbox, [lng, lat]) => {
    if (bbox?.length !== 4) {
        return false;
    }

    const lngs = [bbox[0], bbox[2]].map(parseFloat);
    const lats = [bbox[1], bbox[3]].map(parseFloat);

    return lng >= Math.min(...lngs) && lng <= Math.max(...lngs) && lat >= Math.min(...lats) && lat <= Math.max(...lats);
};

// A settlement: a town, or its commune (an administrative boundary the geocoder types as an address)
const _isLocality = (place) => place.placeType === 'place' || place.osmType === 'administrative';

// An area wider than a town (a country, a canton, a county, a district) never absorbs a namesake: "luxembourg" ranked
// the country first and the city inside its extent vanished. Photon's osm_value, or its type for an administrative boundary
const WIDE_AREAS = new Set(['country', 'state', 'province', 'region', 'county', 'district', 'municipality', 'borough', 'city_district']);

const _absorbsNamesakes = (place) => _isLocality(place) && !WIDE_AREAS.has(place.osmType) && !WIDE_AREAS.has(place.type);

// A point named after a town (its station, its ferry terminal) is that town again
const _isNamedAfterTown = (place) => ['poi', 'other'].includes(place.placeType);

// Against a kept locality only: two streets of the same name in neighbouring towns, or a peak and a village, stay distinct
const _isNamesake = (kept, candidate) => {
    if (!_absorbsNamesakes(kept) || !(_isLocality(candidate) || _isNamedAfterTown(candidate))) {
        return false;
    }

    if (_normalizeName(kept.name) !== _normalizeName(candidate.name) || !kept.center || !candidate.center) {
        return false;
    }

    return _distanceKm(kept.center, candidate.center) <= NAMESAKE_RADIUS_KM || _inBbox(kept.bbox, candidate.center);
};

// Places in rank order: a namesake of a better ranked place nearby (a town and its commune) is the same answer
export const dedupNearbyPlaces = (places = []) => places.reduce((kept, candidate) => {
    if (!kept.some((place) => _isNamesake(place, candidate))) {
        kept.push(candidate);
    }

    return kept;
}, []);

// Measures worth showing first for an activity: pace for running, speed for bike, climb for hiking
export const rideMetricKeys = (ride) => {
    const hasClimb = parseFloat(ride.positiveElevation) > 0;
    let keys;

    if (ride.rideType === 'hiking') {
        keys = ['distance', 'elevation', 'duration'];
    } else if (PaceRideTypes.includes(ride.rideType) && ride.rideType !== 'walk') {
        keys = ['distance', 'duration', 'pace', 'elevation'];
    } else if (ride.rideType === 'bike') {
        keys = ['distance', 'duration', 'speed', 'elevation'];
    } else {
        keys = ['distance', 'duration', 'elevation'];
    }

    return keys.filter((key) => {
        if (key === 'elevation') {
            return hasClimb;
        }

        if (key === 'pace' || key === 'speed') {
            return parseFloat(ride.speed) > 0;
        }

        return true;
    });
};

// Text split around the typed query (accents and case ignored), to render as text: names are user content
export const highlightParts = (text = '', typed = '') => {
    const value = String(text ?? '');
    // Hyphens and apostrophes read as spaces ("saint jean" finds "Saint-Jean"), one character for one
    const fold = (string) => string.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[-_'’]/g, ' ').toLowerCase();
    const query = fold(String(typed ?? '').trim());
    const foldedValue = fold(value);
    const start = query ? foldedValue.indexOf(query) : -1;

    // Folding must keep the length, or the indexes would not map back onto the text
    if (start === -1 || foldedValue.length !== value.length) {
        return [{text: value, match: false}];
    }

    return [
        {text: value.slice(0, start), match: false},
        {text: value.slice(start, start + query.length), match: true},
        {text: value.slice(start + query.length), match: false}
    ].filter((part) => part.text);
};
